import React from "react";
import { ImportSteps, ValidationRow, SummaryStat } from "../../../../shared/components/ImportSteps";

interface ImportStepsProductMaterialProps {
    currentStep: "file" | "validation" | "confirmation";
    selectedFile: File | null;
    importMode: "flush" | "update" | null;
    onFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onImportModeChange: (mode: "flush" | "update") => void;
    onNext: () => void;
    fileInputRef: React.RefObject<HTMLInputElement>;
    isUploading: boolean;
    hasValidationErrors: boolean;
    validatedRows: ValidationRow[];
    onBack: () => void;
    onImport: () => void;
    originalRowCount?: number;
}

export const ImportStepsProductMaterial = (props: ImportStepsProductMaterialProps) => {
    const { originalRowCount, validatedRows, ...restProps } = props;
    
    // Calculate summary stats
    const validRows = validatedRows.filter((r) => r.isValid);
    const invalidRows = validatedRows.filter((r) => !r.isValid);
    const totalRowsProcessed = originalRowCount || validatedRows.length;
    
    // Count all errors
    const totalErrorsCount = validatedRows.reduce((sum, row) => sum + (row.errors?.length || 0), 0);
    
    const summaryStats: SummaryStat[] = [
        {
            value: validRows.length,
            label: "Dòng hợp lệ",
            backgroundColor: "#e8f5e9",
            borderColor: "#4caf50",
            textColor: "#2e7d32",
        },
        {
            value: totalErrorsCount,
            label: "Lỗi",
            backgroundColor: totalErrorsCount > 0 ? "#ffcdd2" : "#e8f5e9",
            borderColor: totalErrorsCount > 0 ? "#f44336" : "#4caf50",
            textColor: totalErrorsCount > 0 ? "#c62828" : "#2e7d32",
        },
        {
            value: invalidRows.length,
            label: "Dòng không hợp lệ",
            backgroundColor: invalidRows.length > 0 ? "#ffcdd2" : "#e8f5e9",
            borderColor: invalidRows.length > 0 ? "#f44336" : "#4caf50",
            textColor: invalidRows.length > 0 ? "#c62828" : "#2e7d32",
        },
        {
            value: totalRowsProcessed,
            label: "Tổng số dòng",
            backgroundColor: "#e3f2fd",
            borderColor: "#2196f3",
            textColor: "#1565c0",
        },
    ];
    
    const fileFormatInstructions = (
        <>
            <div style={{ fontWeight: 500, marginBottom: "4px" }}>Lưu ý về định dạng file:</div>
            <ul style={{ margin: 0, paddingLeft: "20px" }}>
                <li>Dòng 1: Headers tiếng Việt (có thể merge cells)</li>
                <li>Dòng 2: Headers tiếng Anh</li>
                <li>Dòng 3: Tên trường database (product_code, name, category, etc.)</li>
                <li>Dòng 4+: Dữ liệu BOM (mỗi dòng = một quan hệ sản phẩm-vật tư)</li>
                <li>Chỉ sử dụng sheet đầu tiên</li>
                <li>Trường bắt buộc: product_name (Tên sản phẩm - cột 2), material_code (Mã vật tư - cột 6)</li>
                <li>
                    Các trường sản phẩm: product_code (Mã sản phẩm), product_name (Tên sản phẩm), 
                    product_category (Danh mục), product_category_group (Nhóm sản phẩm), storage (Kho số - chỉ "Kho 1" hoặc "Kho 2")
                </li>
                <li>
                    Các trường vật tư: material_code (Mã vật tư), material_name (Tên vật tư), 
                    material_category (Hạng mục - NVLC/NVLP)
                </li>
                <li>
                    Các trường quan hệ ProductMaterial: category_note (Ghi chú hạng mục), 
                    specification (Quy cách vật tư), quantity (Số lượng), measurement (Định lượng), 
                    total_measurement (Tổng định lượng), note (Ghi chú vật tư)
                </li>
                <li>
                    Category: NVLC → "Vật tư chính", NVLP → "Vật tư phụ"
                </li>
                <li>
                    Nếu sản phẩm hoặc vật tư chưa tồn tại, hệ thống sẽ tự động tạo mới
                </li>
            </ul>
        </>
    );

    return (
        <ImportSteps
            {...restProps}
            validatedRows={validatedRows}
            title="Nhập định mức vật tư (BOM)"
            summaryStats={summaryStats}
            templateUrl="/templates/product_material_import.xlsx"
            importModeLabels={{
                update: {
                    title: "Cập nhật và thêm mới",
                    description: "Sản phẩm và quan hệ sản phẩm-vật tư mới sẽ được thêm vào. Sản phẩm trùng sẽ được cập nhật.",
                },
                flush: {
                    title: "Xóa tất cả và nhập mới",
                    description: "Tất cả sản phẩm và quan hệ sản phẩm-vật tư hiện có sẽ bị xóa vĩnh viễn và thay thế bằng dữ liệu từ file. Hành động này không thể hoàn tác.",
                    warning: "CẢNH BÁO",
                },
            }}
            fileFormatInstructions={fileFormatInstructions}
            previewColumns={[
                { key: "product_code", label: "Mã sản phẩm" },
                { key: "product_name", label: "Tên sản phẩm" },
                { key: "product_category", label: "Danh mục SP" },
                { key: "product_category_group", label: "Nhóm sản phẩm" },
                { key: "storage", label: "Kho" },
                { key: "material_code", label: "Mã vật tư" },
                { key: "material_name", label: "Tên vật tư" },
                { key: "material_category", label: "Hạng mục VT" },
                { key: "category_note", label: "Ghi chú hạng mục" },
                { key: "specification", label: "Quy cách" },
                { key: "measurement_unit", label: "Đơn vị" },
                { key: "quantity", label: "Số lượng" },
                { key: "measurement", label: "Định lượng" },
                { key: "total_measurement", label: "Tổng định lượng" },
                { key: "note", label: "Ghi chú" },
            ]}
        />
    );
};

